/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { DeliveryOptionsCartLineAddress } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import React, { useEffect, useState } from 'react';

export interface ICheckoutCartLineDeliveryDateProps {
    className?: string;
    isDisabled?: boolean;
    minRangeValue?: number;
    maxRangeValue?: number;
    validationErrorForRange?: string;
    fallbackPattern?: string;
    ariaLabel?: string;
    telemetryContent?: ITelemetryContent;
    disclaimerText?: string;
    heading?: string;
    checkBoxLabel?: string;
    onChangeMinValue?: void;
    id?: string;
    cartLines?: CartLine[];
    groupDeliveryOption?: DeliveryOptionsCartLineAddress;
    onChange(event: React.ChangeEvent<HTMLInputElement>, cartLines: CartLine[]): void;
}

const CheckoutCartLineDeliveryDate: React.FC<ICheckoutCartLineDeliveryDateProps> = ({
    className,
    isDisabled,
    fallbackPattern,
    ariaLabel,
    telemetryContent,
    onChangeMinValue,
    checkBoxLabel,
    disclaimerText,
    minRangeValue,
    maxRangeValue,
    heading,
    cartLines,
    groupDeliveryOption,
    onChange
}) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [isDateEnabled, setIsDateEnabled] = useState(false);
    const [minDate, setMinDate] = useState('');
    const [maxDate, setMaxDate] = useState('');

    useEffect(() => {
        const today = new Date();

        // Calculate minDate: default to today if minRangeValue is undefined
        const minDateObj = new Date(today);
        minDateObj.setDate(today.getDate() + (minRangeValue ?? 0)); // Defaults to today
        setMinDate(minDateObj.toISOString().split('T')[0]);

        // Calculate maxDate: only set if maxRangeValue is defined
        if (maxRangeValue !== undefined) {
            const maxDateObj = new Date(today);
            maxDateObj.setDate(today.getDate() + maxRangeValue);
            setMaxDate(maxDateObj.toISOString().split('T')[0]);
        }
    }, [minRangeValue, maxRangeValue]);

    // Function to handle date change
    const handleDateChange = (event: React.FocusEvent<HTMLInputElement>) => {
        const selectedValue = event.target.value;
        // Check if the selected date is allowed
        if (selectedValue >= minDate) {
            setSelectedDate(selectedValue);
            console.log('Selected date is allowed.', selectedValue);
        } else {
            console.log('Selected date is not allowed.');
        }

        if (groupDeliveryOption !== undefined && cartLines !== undefined) {
            const cartLinesDate: CartLine[] = [];
            groupDeliveryOption?.cartLineIds?.map(cartLineId => {
                const tempCartLine = cartLines?.find(cartLine => cartLineId === cartLine?.LineId?.toString());
                if (tempCartLine !== undefined) {
                    cartLinesDate.push(tempCartLine);
                }
            });

            if (cartLinesDate !== undefined) {
                onChange(event, cartLinesDate);
            }
        }
    };

    const handleCheckboxChange = () => {
        setIsDateEnabled(!isDateEnabled);
    };

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.DeliveryOptions);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <div className={className}>
            <div>
                <input
                    className={`${className}__checkbox`}
                    type='checkbox'
                    aria-label={ariaLabel}
                    name='requestDeliveryDate'
                    checked={isDateEnabled}
                    onChange={handleCheckboxChange}
                />
                <span className={`${className}__lbl`}>{checkBoxLabel}</span>
            </div>
            <span className={`${className}__disclaimer`}>{disclaimerText}</span>
            <div className={`${className}__calender`}>
                <span className={`${className}__calender-h`}>{heading}</span>
                <input
                    className={`${className}__calender-c`}
                    type='date'
                    name='dateInput'
                    value={selectedDate}
                    onChange={handleDateChange}
                    min={minDate} // Ensures no past dates are selectable
                    max={maxDate || undefined} // Only set max if defined
                    pattern={fallbackPattern}
                    {...attributes}
                    disabled={isDateEnabled ? isDisabled : true}
                />
            </div>
        </div>
    );
};

export default CheckoutCartLineDeliveryDate;
